/**
 * Форма создания и редактирования смены.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ActionPanel,
  Button,
  Paper,
  ProductSelect,
  OrderFilterSelect,
  EmployeeFilterSelect,
} from '../uikit';
import type { Product } from '../api/products';
import type { Employee } from '../api/employees';
import { findEmployeesByIDs } from '../api/employees';
import type { OrderListItemDTO } from '../api/orders';
import { findProductsByIDs } from '../api/products';
import { findOrdersByIDs } from '../api/orders';
import {
  createShift,
  updateShift,
  findShiftsByIds,
  SHIFT_STATUSES,
  SHIFT_STATUS_LABELS,
} from '../api/shifts';
import { toUserMessage } from '../api/client';

const INTEGER_RE = /^\d*$/;

function focusFirstInvalidField(fieldOrder: readonly string[]): void {
  const firstInvalidId = fieldOrder.find(
    (fieldId) => document.getElementById(fieldId)?.getAttribute('aria-invalid') === 'true',
  );
  if (!firstInvalidId) return;
  const el = document.getElementById(firstInvalidId) as HTMLInputElement | HTMLSelectElement | null;
  el?.focus();
}

const FORM_FIELD_ORDER = ['shift-date', 'shift-status'] as const;

const FormField = styled.div`
  margin-bottom: ${({ theme }) => theme.space[2]}px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space[1]}px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FormInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  max-width: 400px;
  padding: ${({ theme }) => theme.space[1]}px 12px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-family: inherit;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.divider)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.error.main : theme.colors.primary.main};
  }

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const FormTextarea = styled.textarea<{ $error?: boolean }>`
  width: 100%;
  max-width: 400px;
  padding: ${({ theme }) => theme.space[1]}px 12px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-family: inherit;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.divider)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text.primary};
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.error.main : theme.colors.primary.main};
  }

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const FormInputQuantity = styled(FormInput)`
  max-width: 80px;

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const ErrorMessage = styled.div`
  margin-top: ${({ theme }) => theme.space[0]}px;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  color: ${({ theme }) => theme.colors.error.main};
`;

const PageTitle = styled.h2`
  margin: 0 0 24px;
  font-size: ${({ theme }) => theme.fontSizes[9]}px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LoadingText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[1]}px;
  flex-wrap: wrap;

  ${({ theme }) => theme.device.mobile} {
    & > button {
      width: 100%;
    }
  }
`;

const ItemsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const ItemRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.highlight.gray25};
  border-radius: ${({ theme }) => theme.borderRadius}px;
`;

const ItemRowActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;

  ${({ theme }) => theme.device.mobile} {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }
`;

const StatusSelect = styled.select`
  padding: ${({ theme }) => theme.space[1]}px 12px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  min-width: 180px;

  ${({ theme }) => theme.device.mobile} {
    width: 100%;
    min-width: 0;
  }
`;

const ItemProductField = styled.div`
  flex: 1 1 220px;
  min-width: min(220px, 100%);
`;

interface FormItemRow {
  id: string;
  /** Id позиции смены из API (только редактирование) */
  shiftItemId?: string;
  product: Product | null;
  planQty: string;
  planQtyError?: string;
}

function generateRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function ShiftFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [order, setOrder] = useState<OrderListItemDTO | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [shiftDate, setShiftDate] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<string>('accepted');
  const [items, setItems] = useState<FormItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [shiftDateError, setShiftDateError] = useState<string | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [employeeError, setEmployeeError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setFetchLoading(true);
    setLoadError(null);

    findShiftsByIds([id])
      .then(async (res) => {
        if (cancelled) return;
        const s = res.items[id] ?? null;
        if (!s) {
          setLoadError('Смена не найдена');
          return;
        }

        setShiftDate(s.shiftDate);
        setDescription(s.description ?? '');
        setStatus(s.status || 'accepted');

        // Load order and employee display info in parallel
        const loadOrderPromise = findOrdersByIDs([s.orderId])
          .then((r) => {
            const o = r.items[s.orderId];
            if (o && !cancelled) {
              setOrder(o as OrderListItemDTO);
            }
          })
          .catch(() => {
            // Fallback: use a synthetic order object with just the ID for display
            if (!cancelled) {
              setOrder({
                id: s.orderId,
                title: '—',
                customer: '',
                quantity: 0,
                date: '',
                status: '',
                createdAt: '',
                updatedAt: '',
              });
            }
          });

        const shiftItems = s.items ?? [];
        const loadEmployeePromise = findEmployeesByIDs([s.employeeId])
          .then((r) => {
            const emp = r.items[s.employeeId];
            if (emp && !cancelled) {
              setEmployee(emp);
            }
          })
          .catch(() => {
            if (!cancelled) {
              setEmployee({ id: s.employeeId });
            }
          });

        const loadProductsPromise =
          shiftItems.length > 0
            ? findProductsByIDs([...new Set(shiftItems.map((it) => it.productId))]).then(
                (r) => r.items as Record<string, Product>,
              )
            : Promise.resolve<Record<string, Product>>({});

        const [, , productsMap] = await Promise.all([
          loadOrderPromise,
          loadEmployeePromise,
          loadProductsPromise,
        ]);

        if (!cancelled) {
          setItems(
            shiftItems.map((it) => ({
              id: generateRowId(),
              shiftItemId: it.id,
              product: (productsMap[it.productId] ?? null) as Product | null,
              planQty: String(it.planQty),
            })),
          );
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(toUserMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  useEffect(() => {
    if (isEdit) return;
    if (items.length === 0) {
      setItems([{ id: generateRowId(), product: null, planQty: '' }]);
    }
  }, [isEdit, items.length]);

  const addItemRow = useCallback(() => {
    setItems((prev) => [...prev, { id: generateRowId(), product: null, planQty: '' }]);
  }, []);

  const removeItemRow = useCallback((rowId: string) => {
    setItems((prev) => prev.filter((r) => r.id !== rowId));
  }, []);

  const updateItemRow = useCallback((rowId: string, patch: Partial<FormItemRow>) => {
    setItems((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));
  }, []);

  const validateItems = useCallback((): boolean => {
    let allValid = true;
    const updates: Array<{ id: string; patch: Partial<FormItemRow> }> = [];

    for (const row of items) {
      const patch: Partial<FormItemRow> = {};
      if (!row.product?.id) continue;

      const qty = parseInt(row.planQty, 10);
      if (row.planQty === '' || !Number.isInteger(qty) || qty <= 0) {
        patch.planQtyError = 'Введите целое число > 0';
        allValid = false;
      } else {
        patch.planQtyError = undefined;
      }
      updates.push({ id: row.id, patch });
    }

    setItems((prev) =>
      prev.map((r) => {
        const u = updates.find((x) => x.id === r.id);
        return u ? { ...r, ...u.patch } : r;
      }),
    );
    return allValid;
  }, [items]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShiftDateError(null);
    setOrderError(null);
    setEmployeeError(null);
    setServerError(null);

    let hasErrors = false;

    if (!shiftDate.trim()) {
      setShiftDateError('Введите дату смены');
      hasErrors = true;
    }
    if (!order?.id) {
      setOrderError('Выберите заказ');
      hasErrors = true;
    }
    if (!employee?.id) {
      setEmployeeError('Выберите сотрудника');
      hasErrors = true;
    }

    const validItems = items.filter((r) => r.product?.id);
    const allItemsValid = validateItems();

    if (hasErrors || !allItemsValid) {
      if (validItems.length === 0) {
        setServerError('Добавьте хотя бы одну позицию с изделием');
      }
      focusFirstInvalidField(FORM_FIELD_ORDER);
      return;
    }

    if (validItems.length === 0) {
      setServerError('Добавьте хотя бы одну позицию с изделием');
      return;
    }

    const apiItems = validItems.map((r) => ({
      ...(isEdit && r.shiftItemId ? { id: r.shiftItemId } : {}),
      productId: r.product!.id,
      planQty: parseInt(r.planQty, 10),
    }));

    setLoading(true);
    try {
      if (isEdit && id) {
        await updateShift({
          id,
          orderId: order!.id,
          employeeId: employee!.id,
          shiftDate: shiftDate.trim(),
          description: description.trim() || undefined,
          status,
          items: apiItems,
        });
        navigate(`/shifts/${id}`);
      } else {
        const created = await createShift({
          orderId: order!.id,
          employeeId: employee!.id,
          shiftDate: shiftDate.trim(),
          description: description.trim() || undefined,
          status,
          items: apiItems,
        });
        navigate(`/shifts/${created.id}`);
      }
    } catch (err) {
      setServerError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <LoadingText>Загрузка…</LoadingText>;
  }

  if (isEdit && loadError) {
    return (
      <>
        <ActionPanel defaultPath="/shifts" />
        <Paper padding={24} shadow={2} style={{ marginTop: 16 }}>
          <PageTitle>Редактировать смену</PageTitle>
          <ErrorMessage role="alert" aria-live="polite" style={{ marginBottom: 16 }}>
            {loadError}
          </ErrorMessage>
          <ButtonRow>
            <Button type="button" variant="filled" color="primary" onClick={() => setReloadKey((prev) => prev + 1)}>
              Повторить
            </Button>
          </ButtonRow>
        </Paper>
      </>
    );
  }

  return (
    <Paper padding={24} shadow={2}>
      <PageTitle>{isEdit ? 'Редактировать смену' : 'Новая смена'}</PageTitle>
      <form onSubmit={handleSubmit} noValidate autoComplete="off">
        <FormField>
          <FormLabel htmlFor="shift-order">Заказ *</FormLabel>
          <OrderFilterSelect
            id="shift-order"
            value={order}
            onChange={(o) => {
              setOrder(o ?? null);
              if (orderError) setOrderError(null);
            }}
            placeholder="Выберите заказ"
            aria-label="Заказ"
          />
          {orderError && (
            <ErrorMessage role="alert">{orderError}</ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="shift-employee">Сотрудник *</FormLabel>
          <EmployeeFilterSelect
            id="shift-employee"
            value={employee}
            onChange={(emp) => {
              setEmployee(emp ?? null);
              if (employeeError) setEmployeeError(null);
            }}
            placeholder="Выберите сотрудника"
            aria-label="Сотрудник"
          />
          {employeeError && (
            <ErrorMessage role="alert">{employeeError}</ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="shift-date">Дата смены *</FormLabel>
          <FormInput
            id="shift-date"
            type="date"
            value={shiftDate}
            onChange={(e) => {
              setShiftDate(e.target.value);
              if (shiftDateError) setShiftDateError(null);
            }}
            aria-required
            aria-invalid={Boolean(shiftDateError)}
            aria-describedby={shiftDateError ? 'shift-date-error' : undefined}
            $error={Boolean(shiftDateError)}
          />
          {shiftDateError && (
            <ErrorMessage id="shift-date-error" role="alert">
              {shiftDateError}
            </ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="shift-status">Статус</FormLabel>
          <StatusSelect
            id="shift-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Статус смены"
          >
            {SHIFT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {SHIFT_STATUS_LABELS[s]}
              </option>
            ))}
          </StatusSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="shift-description">Описание</FormLabel>
          <FormTextarea
            id="shift-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Описание смены (необязательно)"
            maxLength={5000}
          />
        </FormField>

        <ItemsSection>
          <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>Позиции</h2>
          {items.map((row) => (
            <ItemRow key={row.id}>
              <ItemProductField>
                <FormLabel htmlFor={`item-product-${row.id}`}>Изделие</FormLabel>
                <ProductSelect
                  value={row.product}
                  onChange={(p) => updateItemRow(row.id, { product: p ?? null })}
                  placeholder="Выберите изделие"
                />
              </ItemProductField>
              <div>
                <FormLabel htmlFor={`item-qty-${row.id}`}>План, шт</FormLabel>
                <FormInputQuantity
                  id={`item-qty-${row.id}`}
                  type="text"
                  inputMode="numeric"
                  value={row.planQty}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || INTEGER_RE.test(v)) {
                      updateItemRow(row.id, { planQty: v, planQtyError: undefined });
                    }
                  }}
                  placeholder="0"
                  $error={Boolean(row.planQtyError)}
                  aria-invalid={Boolean(row.planQtyError)}
                />
                {row.planQtyError && (
                  <ErrorMessage role="alert">{row.planQtyError}</ErrorMessage>
                )}
              </div>
              <ItemRowActions>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  size="s"
                  onClick={() => removeItemRow(row.id)}
                  aria-label="Удалить позицию"
                >
                  Удалить
                </Button>
              </ItemRowActions>
            </ItemRow>
          ))}
          <div style={{ marginTop: 8 }}>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              size="s"
              onClick={addItemRow}
            >
              Добавить позицию
            </Button>
          </div>
        </ItemsSection>

        {serverError && (
          <ErrorMessage role="alert" aria-live="polite" style={{ marginTop: 16 }}>
            {serverError}
          </ErrorMessage>
        )}

        <ButtonRow style={{ marginTop: 24 }}>
          <Button type="submit" variant="filled" color="primary" loading={loading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={() => navigate(isEdit && id ? `/shifts/${id}` : '/shifts')}
          >
            Отмена
          </Button>
        </ButtonRow>
      </form>
    </Paper>
  );
}
